/**
 * Single source of truth for the account password policy on the client.
 *
 * Mirrors the back-end policy (C-Back-End src/auth/dto/password-policy.ts)
 * so every form that sets a password — signup, accept-invite, reset, and
 * change-password — validates identically and never lets through a password
 * the API would reject.
 *
 * Policy: 8–64 characters, and must contain at least one uppercase letter,
 * one lowercase letter, one number, and one special character. Any special
 * character is allowed (no restricted set).
 */
import { z } from 'zod';

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 64;

export const PASSWORD_REGEX =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,64}$/;

export const PASSWORD_REQUIREMENTS_MESSAGE =
    'Password must include an uppercase letter, a lowercase letter, a number, and a special character';

/** Short helper line for display under a password field. */
export const PASSWORD_HINT =
    '8–64 characters, with an uppercase letter, a lowercase letter, a number, and a special character.';

/** Reusable zod schema for a new-password field. */
export const passwordSchema = z
    .string()
    .min(PASSWORD_MIN_LENGTH, `Password must be at least ${PASSWORD_MIN_LENGTH} characters`)
    .max(PASSWORD_MAX_LENGTH, `Password must be ${PASSWORD_MAX_LENGTH} characters or fewer`)
    .regex(PASSWORD_REGEX, PASSWORD_REQUIREMENTS_MESSAGE);
